import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Upload, Trash2, Search, Copy, Loader2, Image as ImageIcon, FileText, X,
} from 'lucide-react';

import { BACKEND_URL } from '@/config';

const MediaLibraryPage = () => {
  const { api } = useAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [search, setSearch] = useState('');
  const [editItem, setEditItem] = useState(null);
  const [previewItem, setPreviewItem] = useState(null);

  const fetchMedia = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      const res = await api.get(`/media?${params}`);
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch { toast.error('Failed to load media'); }
    finally { setLoading(false); }
  }, [api, search]);

  useEffect(() => { fetchMedia(); }, [fetchMedia]);

  const handleUpload = async (e) => {
    const files = e.target.files;
    if (!files?.length) return;
    setUploading(true);
    let uploaded = 0;
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('alt_text', file.name.replace(/\.[^/.]+$/, '').replace(/[-_]/g, ' '));
        await api.post('/media/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        uploaded++;
      } catch {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    if (uploaded > 0) {
      toast.success(`${uploaded} file(s) uploaded`);
      fetchMedia();
    }
    setUploading(false);
    e.target.value = '';
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this file?')) return;
    try {
      await api.delete(`/media/${id}`);
      toast.success('Deleted');
      fetchMedia();
    } catch { toast.error('Delete failed'); }
  };

  const handleUpdateAlt = async () => {
    if (!editItem) return;
    try {
      const formData = new FormData();
      formData.append('alt_text', editItem.alt_text);
      await api.put(`/media/${editItem.id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Alt text updated');
      setEditItem(null);
      fetchMedia();
    } catch { toast.error('Update failed'); }
  };

  const copyUrl = (url) => {
    const full = url.startsWith('/') ? `${BACKEND_URL}${url}` : url;
    navigator.clipboard.writeText(full);
    toast.success('URL copied');
  };

  const isImage = (mime) => mime?.startsWith('image/');

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <DashboardLayout requiredRoles={['super_admin', 'content_manager']}>
      <div className="space-y-6" data-testid="media-library-page">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-[#2a3e5d]">Media Library</h1>
            <p className="text-slate-500 mt-1 text-sm">{total} file{total !== 1 ? 's' : ''}</p>
          </div>
          <label className="cursor-pointer" data-testid="upload-media-btn">
            <Button asChild className="bg-[#20acf6] hover:bg-[#3bb8f7] text-white" disabled={uploading}>
              <span>
                {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
                Upload Files
              </span>
            </Button>
            <input type="file" className="hidden" multiple accept="image/*,.pdf,.svg" onChange={handleUpload} />
          </label>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search files..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            data-testid="media-search-input"
          />
        </div>

        {/* Grid */}
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-[#20acf6]" />
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-20 text-slate-400">
            <ImageIcon className="h-16 w-16 mx-auto mb-3 opacity-30" />
            <p className="text-lg font-medium">No media files yet</p>
            <p className="text-sm">Upload images to use in your pages and courses</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {items.map(item => (
              <Card key={item.id} className="border-slate-200 group overflow-hidden" data-testid={`media-item-${item.id}`}>
                <div
                  className="aspect-square relative bg-slate-50 cursor-pointer"
                  onClick={() => setPreviewItem(item)}
                >
                  {isImage(item.mime_type) ? (
                    <img
                      src={item.url.startsWith('/') ? `${BACKEND_URL}${item.url}` : item.url}
                      alt={item.alt_text || item.filename}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <FileText className="h-12 w-12 text-slate-300" />
                    </div>
                  )}
                  {/* Overlay */}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                    <Button size="icon" variant="secondary" className="h-8 w-8" onClick={(e) => { e.stopPropagation(); copyUrl(item.url); }}>
                      <Copy className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="icon" variant="secondary" className="h-8 w-8" onClick={(e) => { e.stopPropagation(); setEditItem({ ...item }); }}>
                      <FileText className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="icon" variant="destructive" className="h-8 w-8" onClick={(e) => { e.stopPropagation(); handleDelete(item.id); }}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
                <CardContent className="p-2">
                  <p className="text-xs text-slate-700 truncate font-medium">{item.filename}</p>
                  <p className="text-[10px] text-slate-400">{formatSize(item.size_bytes)}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Edit Alt Text Dialog */}
      <Dialog open={!!editItem} onOpenChange={() => setEditItem(null)}>
        <DialogContent data-testid="edit-alt-dialog">
          <DialogHeader><DialogTitle>Edit Alt Text</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-500">File: {editItem?.filename}</p>
            <div>
              <Label>Alt Text (for accessibility)</Label>
              <Input
                value={editItem?.alt_text || ''}
                onChange={e => setEditItem(p => ({ ...p, alt_text: e.target.value }))}
                placeholder="Describe this image..."
                data-testid="alt-text-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditItem(null)}>Cancel</Button>
            <Button className="bg-[#20acf6] hover:bg-[#3bb8f7]" onClick={handleUpdateAlt} data-testid="save-alt-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={!!previewItem} onOpenChange={() => setPreviewItem(null)}>
        <DialogContent className="sm:max-w-2xl" data-testid="media-preview-dialog">
          <DialogHeader>
            <DialogTitle className="truncate">{previewItem?.filename}</DialogTitle>
          </DialogHeader>
          {previewItem && (
            <div className="space-y-3">
              {isImage(previewItem.mime_type) ? (
                <img
                  src={previewItem.url.startsWith('/') ? `${BACKEND_URL}${previewItem.url}` : previewItem.url}
                  alt={previewItem.alt_text || previewItem.filename}
                  className="w-full rounded-lg max-h-[60vh] object-contain bg-slate-50"
                />
              ) : (
                <div className="h-48 flex items-center justify-center bg-slate-50 rounded-lg">
                  <FileText className="h-16 w-16 text-slate-300" />
                </div>
              )}
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div><span className="text-slate-400">Size:</span> {formatSize(previewItem.size_bytes)}</div>
                <div><span className="text-slate-400">Type:</span> {previewItem.mime_type}</div>
                <div className="col-span-2"><span className="text-slate-400">Alt:</span> {previewItem.alt_text || 'None'}</div>
              </div>
              <Button variant="outline" className="w-full" onClick={() => copyUrl(previewItem.url)}>
                <Copy className="h-4 w-4 mr-2" /> Copy URL
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default MediaLibraryPage;
